/**
 * ----------------------------------------------------------------------------------------------------------------------
 * FAVORITES (obľúbené produkty) – iba frontend, uložené v localStorage
 * ----------------------------------------------------------------------------------------------------------------------
 *
 * Ukladá sa pole objektov: [{ handle, id, addedAt }]
 * - handle  => pre neskoršie načítanie produktu cez /products/<handle>.js na stránke obľúbených
 * - id      => product id (informatívne, keby bolo treba)
 * - addedAt => timestamp, aby sa dalo zoradiť od najnovšieho
 *
 * Verejné API: window.ArtraFavorites
 *   .list()               -> pole položiek
 *   .count()              -> počet
 *   .has(handle)          -> true/false
 *   .add(handle, id)      -> pridá
 *   .remove(handle)       -> odstráni
 *   .toggle(handle, id)   -> prepne, vracia true ak je po prepnutí v obľúbených
 *   .clear()              -> vymaže všetko
 *
 * Event: document.addEventListener('favorites:change', (e) => e.detail.count / e.detail.items)
 */

const STORAGE_KEY = 'artra:favorites';
const CHANGE_EVENT = 'favorites:change';

/* ------------------------------------------------------------------ storage */

function read() {
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return [];

    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];

    return parsed.filter((item) => item && typeof item.handle === 'string' && item.handle !== '');
  } catch (error) {
    // privátny režim / plná pamäť / poškodené dáta – radšej prázdny zoznam ako pád skriptu
    return [];
  }
}

function write(items) {
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(items));
  } catch (error) {
    /* ignore */
  }

  document.dispatchEvent(
    new CustomEvent(CHANGE_EVENT, {
      detail: { items, count: items.length },
    })
  );
}

/* ---------------------------------------------------------------------- API */

const Favorites = {
  storageKey: STORAGE_KEY,

  list() {
    return read();
  },

  count() {
    return read().length;
  },

  has(handle) {
    if (!handle) return false;
    return read().some((item) => item.handle === handle);
  },

  add(handle, id) {
    if (!handle) return false;

    const items = read();
    if (items.some((item) => item.handle === handle)) return true;

    items.push({ handle, id: id ? String(id) : null, addedAt: Date.now() });
    write(items);

    return true;
  },

  remove(handle) {
    if (!handle) return false;

    const items = read();
    const next = items.filter((item) => item.handle !== handle);

    if (next.length !== items.length) write(next);

    return false;
  },

  toggle(handle, id) {
    return this.has(handle) ? this.remove(handle) : this.add(handle, id);
  },

  clear() {
    write([]);
  },
};

window.ArtraFavorites = Favorites;

/* ----------------------------------------------------------------------- UI */

function syncButton(button) {
  const handle = button.getAttribute('data-favorite-toggle');
  const isActive = Favorites.has(handle);

  button.classList.toggle('is-active', isActive);
  button.setAttribute('aria-pressed', isActive ? 'true' : 'false');

  const label = isActive
    ? button.getAttribute('data-label-remove')
    : button.getAttribute('data-label-add');

  if (label) button.setAttribute('aria-label', label);
}

function syncCounters(count) {
  document.querySelectorAll('[data-favorites-count]').forEach((element) => {
    element.textContent = count;
  });

  document.querySelectorAll('[data-favorites-badge]').forEach((badge) => {
    badge.classList.toggle('header__favorites-count-badge--hidden', count < 1);
  });

  document.querySelectorAll('[data-favorites-link]').forEach((link) => {
    link.classList.toggle('has-favorites', count > 0);
  });
}

function syncAll(root = document) {
  root.querySelectorAll('[data-favorite-toggle]').forEach(syncButton);
  syncCounters(Favorites.count());
}

/* Kliknutie na srdiečko – delegované, funguje aj pre dynamicky doplnené karty */
document.addEventListener('click', (event) => {
  const button = event.target.closest('[data-favorite-toggle]');
  if (!button) return;

  event.preventDefault();
  event.stopPropagation();

  const handle = button.getAttribute('data-favorite-toggle');
  Favorites.toggle(handle, button.getAttribute('data-favorite-id'));

  button.classList.remove('is-bumped');
  // reflow, aby sa animácia dala prehrať aj pri rýchlom opakovanom kliknutí
  void button.offsetWidth;
  button.classList.add('is-bumped');
});

document.addEventListener(CHANGE_EVENT, () => syncAll());

/* Zmena v inom tabe */
window.addEventListener('storage', (event) => {
  if (event.key === STORAGE_KEY) syncAll();
});

/* Nové karty (load more, filtre, quick buy, section render v editore) */
let syncScheduled = false;

const observer = new MutationObserver((mutations) => {
  if (syncScheduled) return;

  const hasNewNodes = mutations.some((mutation) => mutation.addedNodes.length > 0);
  if (!hasNewNodes) return;

  syncScheduled = true;
  requestAnimationFrame(() => {
    syncScheduled = false;
    syncAll();
  });
});

function boot() {
  syncAll();
  observer.observe(document.body, { childList: true, subtree: true });
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', boot);
} else {
  boot();
}
